import { Model, FaceData } from '../core/model';
import { OrbitController } from '../camera/orbitController';
import {
  Vec3, vec, sub, add, mul, dot, dist, closestPointOnSegment, projectPointOnPlane, nearestAxis, AXIS_X, AXIS_Y, AXIS_Z, normalize
} from '../core/vec';
import { faceBasis, pointInPolygon, toBasis2D } from '../core/face';
import { GuideData } from '../core/model';

/** Snapshot-Typen wie in SketchUp: Endpunkt, Mittelpunkt, Auf-Kante, Auf-Fläche, Hilfslinie, Raster, Ursprung. */
export type SnapKind = 'endpoint' | 'midpoint' | 'onEdge' | 'onFace' | 'onGuide' | 'grid' | 'origin' | 'ground';

export interface SnapResult {
  point: Vec3;
  kind: SnapKind;
  /** Priorität: höher gewinnt bei gleichem Pixelabstand. */
  priority: number;
  faceId?: number;
  edgeId?: number;
  guideId?: number;
  /** Flächenebene des Snaps (für Werkzeuge, die in einer Ebene zeichnen). */
  plane?: { point: Vec3; normal: Vec3 };
}

export interface SnapOptions {
  fallbackPlane?: { point: Vec3; normal: Vec3 } | null;
  /** Vertex-IDs, deren Endpunkte/Kanten/Flächen beim Snapping ignoriert werden (z. B. das gezogene Objekt selbst). */
  excludeVertices?: Set<number>;
  /** Auf-Fläche-Snaps deaktivieren (beim Verschieben, sonst driftet das Objekt frei im Raum). */
  includeFaces?: boolean;
  /** Raster- und Ursprung-Snaps auf der Fallback-Ebene (Default: an). */
  grid?: boolean;
}

const SNAP_RADIUS_PX = 12;
/** Abstand Cursor ↔ Achsgerade, ab dem eine Linie auf der Achse einrastet. */
const AXIS_SNAP_PX = 14;
/** Winkel-Toleranz (Bildschirm) zwischen Mausrichtung und Achse für die Achs-Inferenz. */
const AXIS_SNAP_DEG = 7;
/** Rasterschritt: Kreuzungspunkte (1 m) und Zellenmitten (0,5 m) des Bodengitters. */
export const GRID_STEP_M = 0.5;

const ORIGIN = vec();

export class InferenceEngine {
  constructor(
    private model: Model,
    private camera: OrbitController
  ) {}

  /**
   * Findet den besten Snapshot für Cursorposition (CSS-Pixel) + Blickstrahl.
   * Ohne Treffer: Fußpunkt auf der Ebene `fallbackPlane` (sonst Boden z=0).
   */
  snap(cursorX: number, cursorY: number, ray: { origin: Vec3; dir: Vec3 }, opts?: SnapOptions): SnapResult {
    const candidates: SnapResult[] = [];
    const exclude = opts?.excludeVertices;

    // Ausgeblendete Ebenen fangen nicht; Vertex-Sichtbarkeit über die sichtbaren Kanten bestimmen
    const visibleVerts = new Set<number>();
    for (const e of this.model.edges.values()) {
      if (!this.model.isEntityVisible(e.group)) continue;
      visibleVerts.add(e.a);
      visibleVerts.add(e.b);
    }

    // Endpunkte
    for (const v of this.model.vertices.values()) {
      if (exclude?.has(v.id) || !visibleVerts.has(v.id)) continue;
      const d = this.camera.screenDistance(v.p, cursorX, cursorY);
      if (d <= SNAP_RADIUS_PX) candidates.push({ point: v.p, kind: 'endpoint', priority: 5 });
    }
    // Mittelpunkte + Auf-Kante
    for (const e of this.model.edges.values()) {
      if (exclude?.has(e.a) || exclude?.has(e.b) || !this.model.isEntityVisible(e.group)) continue;
      const [a, b] = this.model.edgePoints(e);
      const mid = mul(add(a, b), 0.5);
      const dMid = this.camera.screenDistance(mid, cursorX, cursorY);
      if (dMid <= SNAP_RADIUS_PX) candidates.push({ point: mid, kind: 'midpoint', priority: 4, edgeId: e.id });
      // nächster Punkt auf der Kante entlang des Blickstrahls
      const onEdge = closestPointOnRaySegment(ray, a, b);
      if (onEdge) {
        const d = this.camera.screenDistance(onEdge.point, cursorX, cursorY);
        if (d <= SNAP_RADIUS_PX) {
          candidates.push({ point: onEdge.point, kind: 'onEdge', priority: 2, edgeId: e.id });
        }
      }
    }
    // Verdeckung wie SketchUp: Punkte hinter der vordersten Fläche fangen nicht.
    // Eckpunkte der vordersten Fläche selbst bleiben immer fangbar.
    let front = Infinity;
    let frontFace: FaceData | null = null;
    for (const f of this.model.faces.values()) {
      if (!this.model.isEntityVisible(f.group)) continue;
      if (exclude && (f.outer.some((id) => exclude.has(id)) || f.holes.some((h) => h.some((id) => exclude.has(id))))) continue;
      const t = rayFaceDistance(this.model, f, ray);
      if (t !== null && t < front) { front = t; frontFace = f; }
    }
    const frontVerts = new Set(frontFace ? [...frontFace.outer, ...frontFace.holes.flat()] : []);
    const tol = Math.max(1e-4, front * 2e-3);
    // Auf-Fläche: nur die vorderste Fläche unter dem Cursor (Löcher ausgenommen)
    if (opts?.includeFaces !== false && frontFace) {
      const outer = frontFace.outer.map((id) => this.model.vertexPosition(id));
      const basis = faceBasis(outer);
      // nicht bei fast streifendem Blick
      if (Math.abs(dot(ray.dir, basis.n)) >= 0.02) {
        const hit = add(ray.origin, mul(ray.dir, front));
        candidates.push({ point: hit, kind: 'onFace', priority: 1, faceId: frontFace.id, plane: { point: basis.origin, normal: basis.n } });
      }
    }
    // Hilfslinien
    for (const g of this.model.guides.values()) {
      const hit = closestPointOnGuide(ray, g);
      if (hit) {
        const d = this.camera.screenDistance(hit, cursorX, cursorY);
        if (d <= SNAP_RADIUS_PX) candidates.push({ point: hit, kind: 'onGuide', priority: 2.5, guideId: g.id });
      }
    }
    // Raster + Ursprung auf der Fallback-Ebene (Boden bzw. Bewegungsebene)
    if (opts?.grid !== false) {
      const plane = opts?.fallbackPlane ?? { point: vec(), normal: AXIS_Z };
      const hit = projectPointOnPlane(ray.origin, ray.dir, plane.point, plane.normal);
      if (hit && dot(sub(hit, ray.origin), ray.dir) >= 0) {
        // In Ebenenkoordinaten runden → Rasterpunkt bleibt auf der Ebene (auch senkrecht/schräg)
        const [gu, gv] = gridAxes(plane.normal);
        const du = dot(hit, gu) / GRID_STEP_M, dv = dot(hit, gv) / GRID_STEP_M;
        const gp = add(hit, add(mul(gu, (Math.round(du) - du) * GRID_STEP_M), mul(gv, (Math.round(dv) - dv) * GRID_STEP_M)));
        const d = this.camera.screenDistance(gp, cursorX, cursorY);
        if (d <= SNAP_RADIUS_PX) {
          candidates.push({ point: gp, kind: 'grid', priority: 1.2, plane: { point: { ...plane.point }, normal: plane.normal } });
        }
      }
      const dO = this.camera.screenDistance(ORIGIN, cursorX, cursorY);
      if (dO <= SNAP_RADIUS_PX) candidates.push({ point: ORIGIN, kind: 'origin', priority: 4.5, plane: { point: vec(), normal: AXIS_Z } });
    }

    const visible = (c: SnapResult): boolean => {
      // Hinter der Kamera liegende Punkte werden gespiegelt projiziert → nie fangen
      if (dot(sub(c.point, ray.origin), ray.dir) <= 0) return false;
      if (!Number.isFinite(front) || c.kind === 'onFace') return true;
      if (c.edgeId !== undefined) {
        const e = this.model.edges.get(c.edgeId);
        if (e && frontVerts.has(e.a) && frontVerts.has(e.b)) return true;
      }
      if (c.kind === 'endpoint' && [...frontVerts].some((id) => this.model.vertices.get(id)?.p === c.point)) return true;
      return dot(sub(c.point, ray.origin), ray.dir) <= front + tol;
    };

    // Bester Kandidat: höchste Priorität, dann kleinster Pixelabstand
    let best: SnapResult | null = null;
    let bestScore = -Infinity;
    for (const c of candidates) {
      if (!visible(c)) continue;
      const d = this.camera.screenDistance(c.point, cursorX, cursorY);
      const score = c.priority * 1000 - d;
      if (score > bestScore) { bestScore = score; best = c; }
    }
    if (best) return best;

    // Fallback: Ebene schneiden (gegebene Flächenebene oder Boden)
    const plane = opts?.fallbackPlane ?? { point: vec(0, 0, 0), normal: AXIS_Z };
    const hit = projectPointOnPlane(ray.origin, ray.dir, plane.point, plane.normal);
    const p = hit ?? projectPointOnPlane(ray.origin, ray.dir, vec(), AXIS_Z) ?? vec();
    return { point: p, kind: 'ground', priority: 0, plane };
  }

  /**
   * Achs-Inferenz wie SketchUp („Auf roter/grüner/blauer Achse“): nächster Punkt der
   * Achsgeraden durch `start` zum Blickstrahl. Mit `locked` immer diese Achse, sonst die
   * Achse, deren Gerade dem Cursor am nächsten ist (≤ AXIS_SNAP_PX). Funktioniert auch
   * senkrecht (blau), unabhängig von Boden oder Flächen.
   */
  axisInference(
    start: Vec3,
    cursorX: number,
    cursorY: number,
    ray: { origin: Vec3; dir: Vec3 },
    locked: 0 | 1 | 2 | null
  ): { point: Vec3; axis: 0 | 1 | 2 } | null {
    const axes: (0 | 1 | 2)[] = locked !== null ? [locked] : [0, 1, 2];
    const s0 = this.camera.projectToScreen(start);
    const mx = cursorX - s0.x, my = cursorY - s0.y;
    const ml = Math.hypot(mx, my);
    let best: { point: Vec3; axis: 0 | 1 | 2; d: number } | null = null;
    for (const axis of axes) {
      const dirV = [AXIS_X, AXIS_Y, AXIS_Z][axis]!;
      const p = closestPointOnLineToRay(start, dirV, ray);
      if (!p) continue;
      if (dist(p, start) < 1e-6) continue;
      const d = this.camera.screenDistance(p, cursorX, cursorY);
      if (locked === null) {
        // Nah an der Achsgeraden ODER Mausrichtung (vom Start aus) fast parallel zur Achse
        const s1 = this.camera.projectToScreen(add(start, mul(dirV, 0.1)));
        const ax = s1.x - s0.x, ay = s1.y - s0.y;
        const al = Math.hypot(ax, ay);
        const sin = al > 1e-6 && ml > 1e-6 ? Math.abs(ax * my - ay * mx) / (al * ml) : 1;
        const byAngle = ml > AXIS_SNAP_PX && sin < Math.sin((AXIS_SNAP_DEG * Math.PI) / 180);
        if (d > AXIS_SNAP_PX && !byAngle) continue;
      }
      if (!best || d < best.d) best = { point: p, axis, d };
    }
    return best ? { point: best.point, axis: best.axis } : null;
  }

  /**
   * Achsen-Sperre für eine Zeichnung von `start` nach `current`:
   * - hart gesperrt, wenn `locked` (0=X,1=Y,2=Z)
   * - sonst automatisch, wenn die Richtung nah an einer Achse liegt (±4,5°)
   * Liefert den gesnappten Punkt und die Achse (null, wenn frei).
   */
  axisLock(start: Vec3, current: Vec3, locked: 0 | 1 | 2 | null): { point: Vec3; axis: 0 | 1 | 2 | null } {
    const delta = sub(current, start);
    if (locked !== null) {
      return { point: projectOnAxis(start, current, locked), axis: locked };
    }
    const near = nearestAxis(delta);
    if (near.angle < (4.5 * Math.PI) / 180 && dist(start, current) > 1e-4) {
      return { point: projectOnAxis(start, current, near.axis), axis: near.axis };
    }
    return { point: current, axis: null };
  }
}

/** Projiziert `current` auf die Achsgerade durch `start` (0=X,1=Y,2=Z). */
export function projectOnAxis(start: Vec3, current: Vec3, axis: 0 | 1 | 2): Vec3 {
  const p = { ...current };
  if (axis === 0) { p.y = start.y; p.z = start.z; }
  else if (axis === 1) { p.x = start.x; p.z = start.z; }
  else { p.x = start.x; p.y = start.y; }
  return p;
}

/**
 * Bewegungs-Delta für das Verschieben:
 * - Diskrete Punkte (Ecke, Mittelpunkt, Ursprung) richten die Objektecke `handle` exakt aus.
 * - Rasterpunkte ebenso, wenn der Griff auf einer Ecke lag (`cornerGrab`); sonst folgt der Griffpunkt `ref`,
 *   projiziert auf die Bewegungsebene `plane` (Raster-Ausrichtung ohne Sprung beim Fassen).
 * - `lockedAxis` (0=X,1=Y,2=Z) schränkt das Delta zusätzlich auf eine Achse ein.
 */
export function computeMoveDelta(
  snap: SnapResult,
  ref: Vec3,
  handle: Vec3,
  plane: { point: Vec3; normal: Vec3 } | null,
  lockedAxis: 0 | 1 | 2 | null,
  cornerGrab: boolean
): Vec3 {
  const alignHandle =
    snap.kind === 'endpoint' || snap.kind === 'midpoint' || snap.kind === 'origin' || (snap.kind === 'grid' && cornerGrab);
  let delta: Vec3;
  if (alignHandle) {
    delta = sub(snap.point, handle);
  } else {
    delta = sub(snap.point, ref);
    if (plane && lockedAxis === null) {
      delta = sub(delta, mul(plane.normal, dot(delta, plane.normal)));
    }
  }
  if (lockedAxis !== null) {
    const axis = [AXIS_X, AXIS_Y, AXIS_Z][lockedAxis]!;
    delta = mul(axis, dot(delta, axis));
  }
  return delta;
}

/** Nächster Punkt zwischen Strahl und Strecke (3D). */
export function closestPointOnRaySegment(
  ray: { origin: Vec3; dir: Vec3 },
  a: Vec3,
  b: Vec3
): { point: Vec3; distance: number } | null {
  const ab = sub(b, a);
  const abLenSq = dot(ab, ab);
  if (abLenSq < 1e-12) return null;
  // Strahl: O + t·D, Strecke: A + s·AB
  const w = sub(ray.origin, a);
  const dDotAb = dot(ray.dir, ab);
  const dDotD = dot(ray.dir, ray.dir);
  const s = (dDotAb * dot(w, ray.dir) - dot(w, ab) * dDotD) / (dDotAb * dDotAb - dDotD * abLenSq);
  const sClamped = Math.min(1, Math.max(0, s));
  const t = (sClamped * dDotAb - dot(w, ray.dir)) / dDotD;
  if (t < 0) return null;
  const segPoint = add(a, mul(ab, sClamped));
  const rayPoint = add(ray.origin, mul(ray.dir, t));
  return { point: segPoint, distance: dist(rayPoint, segPoint) };
}

/** Nächster Punkt zwischen Strahl und Hilfslinie. */
function closestPointOnGuide(ray: { origin: Vec3; dir: Vec3 }, g: GuideData): Vec3 | null {
  const dir = normalize(sub(g.b, g.a));
  if (g.infinite) {
    // Strahl (O + t·D) × Gerade (A + s·L): Normalgleichung lösen
    const w = sub(ray.origin, g.a);
    const a = dot(ray.dir, ray.dir);
    const b = dot(ray.dir, dir);
    const d = dot(w, ray.dir);
    const e = dot(w, dir);
    const denom = b * b - a;
    if (Math.abs(denom) < 1e-12) return null;
    const s = (d * b - e * a) / denom;
    const t = (s * b - d) / a;
    if (t < 0) return null;
    return add(g.a, mul(dir, s));
  }
  const hit = closestPointOnRaySegment(ray, g.a, g.b);
  return hit ? hit.point : null;
}

/** Punkt auf der Geraden start + t·axis, der dem Strahl am nächsten liegt (t beliebig). */
export function closestPointOnLineToRay(start: Vec3, axis: Vec3, ray: { origin: Vec3; dir: Vec3 }): Vec3 | null {
  const a = normalize(axis);
  const d = normalize(ray.dir);
  const w0 = sub(start, ray.origin);
  const b = dot(a, d);
  const denom = 1 - b * b;
  if (denom < 1e-9) return null;
  const t = (b * dot(d, w0) - dot(a, w0)) / denom;
  return add(start, mul(a, t));
}

/** Abstand entlang des Strahls bis zur Fläche (innerhalb des Rings, außerhalb von Löchern), sonst null. */
function rayFaceDistance(model: Model, f: FaceData, ray: { origin: Vec3; dir: Vec3 }): number | null {
  const outer = f.outer.map((id) => model.vertexPosition(id));
  if (outer.length < 3) return null;
  const basis = faceBasis(outer);
  const denom = dot(ray.dir, basis.n);
  if (Math.abs(denom) < 1e-9) return null;
  const t = dot(sub(basis.origin, ray.origin), basis.n) / denom;
  if (t <= 0) return null;
  const hit = add(ray.origin, mul(ray.dir, t));
  const q = toBasis2D(basis, hit);
  if (!pointInPolygon(q.x, q.y, outer.map((p) => toBasis2D(basis, p)))) return null;
  for (const h of f.holes) {
    if (pointInPolygon(q.x, q.y, h.map((id) => toBasis2D(basis, model.vertexPosition(id))))) return null;
  }
  return t;
}

/** Zwei orthonormale Rasterachsen in einer Ebene (waagrecht: Welt-X/Y). */
function gridAxes(normal: Vec3): [Vec3, Vec3] {
  const n = normalize(normal);
  const cands = [AXIS_X, AXIS_Y, AXIS_Z];
  let u = cands[0]!;
  let best = Infinity;
  for (const c of cands) {
    const d = Math.abs(dot(c, n));
    if (d < best - 1e-9) { best = d; u = c; }
  }
  u = normalize(sub(u, mul(n, dot(u, n))));
  const v = normalize({ x: n.y * u.z - n.z * u.y, y: n.z * u.x - n.x * u.z, z: n.x * u.y - n.y * u.x });
  return [u, v];
}
